"use client";

import { type MotionValue, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { StarIcon } from "@heroicons/react/solid";
import Image from "next/image";
import { type CSSProperties, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";

export type PullQuoteLogo = {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Sizing/treatment for the attribution mark (square icon vs. wordmark). */
  className?: string;
};

export type Testimonial = {
  /** Quote text including its surrounding curly quotes. */
  quote: string;
  author: string;
  company: string;
  logo: PullQuoteLogo;
};

const DEFAULT_TESTIMONIAL: Testimonial = {
  quote:
    "\u201CWe've had a fantastic experience with Helios. Their platform is incredibly user friendly, and the team is always so helpful whenever I have a question or need support.\u201D",
  author: "Lisa Shipley",
  company: "Barre3 West Loop",
  logo: {
    src: "/images/barre3-icon.png",
    alt: "Barre3",
    width: 40,
    height: 40,
    className: "h-9 w-9 rounded-md",
  },
};

type Variant = "curtain" | "standalone";

// `curtain` (homepage): a tall pinned section pulled up behind the hero
// (-mt-[100vh]); the hero scrolls away like a curtain to uncover the pinned
// quote, and the word highlight runs once it's fully uncovered (so it starts
// late, ~0.36). `standalone` (inner pages): a normal-height band — no pinning —
// whose highlight is driven by the quote travelling through the viewport.
const VARIANTS: Record<
  Variant,
  {
    revealStart: number;
    revealEnd: number;
    // useScroll target/offset differ: the curtain measures the whole tall
    // section against the viewport top; the standalone measures the figure
    // crossing the viewport.
    offset: [string, string];
  }
> = {
  curtain: {
    revealStart: 0.36,
    revealEnd: 0.62,
    offset: ["start start", "end start"],
  },
  standalone: {
    revealStart: 0.12,
    revealEnd: 0.5,
    offset: ["start end", "end start"],
  },
};

// Un-revealed words are a SOLID subtle color (like Attio) rather than a
// transparent foreground — otherwise the dotted backdrop bleeds through the
// glyphs. color-mix keeps it theme-aware: a faint tint of the text color.
const FADED_COLOR = "color-mix(in srgb, hsl(var(--foreground)) 22%, hsl(var(--background)))";

function Word({
  children,
  progress,
  range,
  reduce,
}: {
  children: string;
  progress: MotionValue<number>;
  range: [number, number];
  reduce: boolean;
}) {
  const opacity = useTransform(progress, range, [0, 1]);
  return (
    <>
      <span className="relative inline-block whitespace-nowrap">
        {/* Solid faded base — always visible, opaque over the dots. */}
        <span style={{ color: FADED_COLOR }}>{children}</span>
        {/* Full-color word fades in on top as you scroll. */}
        <motion.span
          aria-hidden
          className="absolute inset-0 text-foreground"
          style={reduce ? { opacity: 1 } : { opacity }}
        >
          {children}
        </motion.span>
      </span>{" "}
    </>
  );
}

export function PullQuote({
  testimonial = DEFAULT_TESTIMONIAL,
  variant = "curtain",
}: {
  testimonial?: Testimonial;
  variant?: Variant;
}) {
  const reduce = useReducedMotion() ?? false;
  const sectionRef = useRef<HTMLElement>(null);
  const figureRef = useRef<HTMLElement>(null);

  const { revealStart, revealEnd, offset } = VARIANTS[variant];
  const pinned = variant === "curtain";

  const { scrollYProgress } = useScroll({
    // Curtain measures the tall section; standalone measures the quote itself
    // crossing the viewport so the highlight tracks normal scrolling.
    target: pinned ? sectionRef : figureRef,
    offset: offset as never,
  });

  const revealSpan = revealEnd - revealStart;
  const words = useMemo(() => testimonial.quote.split(" "), [testimonial.quote]);

  // Attribution stays visible the whole time — dimmed while the words are still
  // highlighting, then up to full opacity as the reveal completes.
  const attrOpacity = useTransform(scrollYProgress, [revealEnd - 0.12, revealEnd], [0.4, 1]);

  const { logo } = testimonial;

  const dots = (
    <div
      aria-hidden
      className="bg-grid absolute inset-0"
      style={
        {
          "--canvas-grid-background-color": "transparent",
          "--canvas-grid-foreground-color": "hsl(var(--foreground) / 0.14)",
        } as CSSProperties
      }
    />
  );

  const figure = (
    <figure ref={figureRef} className="relative mx-auto max-w-3xl px-6 text-center">
      <blockquote className="text-balance text-[1.625rem] font-bold leading-[1.3] tracking-tight sm:text-3xl md:text-4xl">
        {words.map((word, i) => {
          const start = revealStart + (i / words.length) * revealSpan;
          const end = revealStart + ((i + 1) / words.length) * revealSpan;
          return (
            <Word
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed static word list
              key={i}
              progress={scrollYProgress}
              range={[start, end]}
              reduce={reduce}
            >
              {word}
            </Word>
          );
        })}
      </blockquote>

      <motion.figcaption
        className="mt-8 flex items-center justify-center gap-3"
        style={reduce ? { opacity: 1 } : { opacity: attrOpacity }}
      >
        <Image
          src={logo.src}
          alt={logo.alt}
          width={logo.width}
          height={logo.height}
          className={cn("w-auto", logo.className)}
        />
        <div className="text-left">
          <div className="text-sm font-semibold leading-tight text-foreground">
            {testimonial.author}
          </div>
          <div className="text-sm leading-tight text-muted-foreground">{testimonial.company}</div>
        </div>
        <div className="mx-1 h-8 w-px bg-border" />
        <div className="flex gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed 5-star list
              key={i}
              className="h-4 w-4 text-amber-400"
            />
          ))}
        </div>
      </motion.figcaption>
    </figure>
  );

  // Curtain: tall, pinned behind the hero.
  if (pinned) {
    return (
      <section
        ref={sectionRef}
        className="relative z-0 -mt-[100vh] h-[300vh] border-b border-border bg-muted/40"
      >
        <div className="sticky top-0 h-screen overflow-hidden">
          {/* Match the header container (max-w-7xl + px) so the vertical rules
              line up with the nav content edges. */}
          <div className="mx-auto h-full max-w-7xl px-6 lg:px-8">
            {/* Bordered center column. The dotted backdrop lives only here — the
                gutters keep the plain subtle-gray section background. */}
            <div className="relative flex h-full items-center justify-center border-x border-border">
              {dots}
              {figure}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Standalone: a normal-height band; the highlight tracks the quote scrolling
  // through the viewport (no pinning, no scroll-jacking).
  return (
    <section ref={sectionRef} className="border-y border-border bg-muted/40">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="relative flex items-center justify-center overflow-hidden border-x border-border py-24 sm:py-28 lg:py-36">
          {dots}
          {figure}
        </div>
      </div>
    </section>
  );
}
